import { useEffect, useState, type FC } from 'react'
import { useForm, Controller } from 'react-hook-form'
import Skeleton, { SkeletonTheme } from 'react-loading-skeleton'
import { useNavigate } from 'react-router'
import {
  useGetEnrollmentHYCSVerifyEmailData,
  useSubmitEnrollmentHYCSResendEmailData,
  useSubmitEnrollmentHYCSVerifyEmailData,
} from '@egw/api'
import { Input, Alert, AlertType, GenericError } from '@egw/components'
import { useTranslation } from '@egw/i18n'
import { Logger } from '@egw/logger'
import { EnrollmentHYCSLayout } from '@egw/layouts'
import { sendEvent } from '@egw/events'
import { CheckIcon } from '@heroicons/react/24/outline'
import { WEALTH_ROUTES } from '@egw/constants'
import { PageHeader } from '../../../components'
import { useValidateRoute } from '../../../hooks/navigation/validate-route'
import { EDataLayerEventWealth } from '../../../constants/events/EDataLayerEventWealth'
import { newPathIsDifferent } from '../../../utils/general/new-path-is-different'
import { ROUTES } from '../../../constants/routes/routes-generic'

const log = new Logger('Enrollment - HYCS - VerifyEmail')

export const VerifyEmailPage: FC = () => {
  const { t } = useTranslation('features-enrollment')
  const navigate = useNavigate()

  const { data, isLoading, error } = useGetEnrollmentHYCSVerifyEmailData()
  const { mutate, isLoading: isSubmitting, error: submitError } = useSubmitEnrollmentHYCSVerifyEmailData()
  const {
    mutate: resend,
    isLoading: isResendSubmitting,
    error: submitResendError,
  } = useSubmitEnrollmentHYCSResendEmailData()

  const [isInvalidToken, setIsInvalidToken] = useState(false)
  const [codeResent, setCodeResent] = useState(false)
  const [counter, setCounter] = useState(0)
  const [hasShownIntent, setHasShownIntent] = useState(false)

  const email = data?.__typename === 'SubmitEnrollmentSuccess' ? data.enrollmentData?.email : ''
  const applicationId = data?.__typename === 'SubmitEnrollmentSuccess' ? data.enrollmentData?.applicationId : ''

  const {
    control,
    watch,
    formState: { errors: formErrors },
  } = useForm({
    defaultValues: {
      emailOtpCode: '',
    },
  })

  // Protect route
  useValidateRoute(data?.path)

  const emailOtpCode = watch('emailOtpCode')

  useEffect(() => {
    if (!hasShownIntent && emailOtpCode) {
      setHasShownIntent(true)
      sendEvent(EDataLayerEventWealth.BANKHYCHYS_VERIFYEMAIL_INTENT, {
        applicationId,
      })
    }
  }, [applicationId, emailOtpCode, hasShownIntent])

  const onSubmit = async (): Promise<void> => {
    setIsInvalidToken(false)
    try {
      const response = await mutate({
        emailOtpCode,
      })
      // Handle different error types
      if (response?.__typename === 'SubmitEnrollmentError') {
        if (response.type === 'INVALID_TOKEN') {
          setIsInvalidToken(true)
        }

        if (response.type === 'APPLICANT_IN_MANUAL_REVIEW' && response.path) {
          void navigate(response.path)
        }

        if (response.type === 'USER_ALREADY_EXISTS' && response.path) {
          void navigate(response.path)
        }
      }
      // If path exists and is different
      if (response?.path && newPathIsDifferent(location.pathname, response.path)) {
        sendEvent(EDataLayerEventWealth.BANKHYCHYS_EMAIL_VERIFIED, {
          applicationId,
        })
        navigate(response.path)
      } else {
        // If no path from BE, redirect to error
        if (!response?.path) {
          navigate(`${WEALTH_ROUTES.ENROLLMENT.path}/${ROUTES.ERROR.path}`)
        }
      }
    } catch (e) {
      log.error('Error submitting form', e)
    }
  }

  useEffect(() => {
    if (emailOtpCode.length === 6) {
      void onSubmit()
    }
  }, [emailOtpCode])

  const resendEmail = (): void => {
    setCodeResent(true)

    sendEvent(EDataLayerEventWealth.BANKHYCHYS_EMAILOTPRESEND_SUBMIT, {
      applicationId,
    })

    setTimeout(() => {
      void sendMagicLinkEmail()
      setCounter(30)
      const intervalId = setInterval(() => {
        setCounter((prev) => {
          if (prev <= 1) {
            clearInterval(intervalId)
            setCodeResent(false)

            return 0
          }
          return prev - 1
        })
      }, 1000)
    }, 5000)
  }

  const sendMagicLinkEmail = async (): Promise<void> => {
    try {
      await resend({
        triggerEmailOtp: true,
      })
    } catch (e) {
      log.error('Resend email error', e)
    }
  }

  return (
    <EnrollmentHYCSLayout title={t('hycs.verify-email.page_title')}>
      <div className="egw-container-enroll-hycs h-full flex flex-col relative md:justify-center justify-start items-start">
        {/* Loading initial data to make sure the user has access to this screen and retreive data based on user enrollment application */}
        {isLoading ? (
          <SkeletonTheme baseColor="#eeefee" highlightColor="rgba(255, 255, 255, 0.5)">
            <Skeleton count={1} height="100px" className="mb-10" />
            <Skeleton count={5} height="50px" />
          </SkeletonTheme>
        ) : null}

        {/* In some cases initial data can be null, so we assume if it's not loading and there's no error - we show the page */}
        {!isLoading && !error ? (
          <form className="w-full flex-1 flex flex-col justify-start lg:justify-center">
            <PageHeader title={t('hycs.verify-email.title')} className="mb-xl" />
            <p className="typo-primary-base-regular text-text-meta mb-3xs">{t('hycs.verify-email.description')}</p>
            <p className="typo-primary-base-semibold text-text-header mb-md">{email}</p>

            <p className="typo-primary-base-regular text-text-meta mb-xl">{t('hycs.verify-email.check_your_email')}</p>
            {isInvalidToken ? (
              <p className="typo-primary-xs-semibold text-text-error">{t('hycs.verify-email.code_expired')}</p>
            ) : null}
            <Controller
              name="emailOtpCode"
              control={control}
              rules={{
                required: t('common.required_field'),
                pattern: {
                  value: /^[0-9]{6}$/,
                  message: t('common.invalid_ssn'),
                },
              }}
              render={({ field: { ref, ...restField } }) => (
                <Input
                  {...restField}
                  label={t('hycs.verify-email.enter_code')}
                  maxLength={6}
                  required
                  error={formErrors.emailOtpCode?.message}
                  onChange={(e) => {
                    const newValue = e.target.value.replace(/[^0-9]/g, '')
                    restField.onChange(newValue)
                  }}
                  testId="emailOtpCode_input"
                />
              )}
            />

            <div className="flex items-baseline gap-4">
              <button
                onClick={resendEmail}
                disabled={isResendSubmitting || codeResent || isSubmitting}
                className={`typo-primary-sm-semibold text-left mt-sm w-fit ${codeResent ? 'text-gray-gray-600' : ' text-text-action'}`}
                type="button"
                data-testid="sendEmailOtp_button"
              >
                {t('hycs.verify-email.resend_email')}
              </button>
              {codeResent && !counter ? (
                <p className="typo-primary-sm-semibold text-text-header-success flex items-center gap-2">
                  {t('hycs.verify-email.code_sent')} <CheckIcon className="size-5" />
                </p>
              ) : null}
            </div>

            {counter ? (
              <p className="typo-primary-sm-semibold text-text-placeholder mt-2xs">
                {t('hycs.verify-email.send_another_code', {
                  time: counter,
                })}
              </p>
            ) : null}

            <div className="flex-1 md:hidden" />

            {submitError ? <Alert alertType={AlertType.Error} description={t('common.generic_error')} className="mt-sm" /> : null}

            {/* AUTO GENERATED COMPONENTS */}
          </form>
        ) : null}

        {/* This error handles the case when the initial data is not loaded, for example network error */}
        {(error ?? submitResendError) ? (
          <div className="flex-1 flex flex-col justify-start md:justify-center">
            <GenericError errorMessage={t('common.generic_error')} />
          </div>
        ) : null}
      </div>
    </EnrollmentHYCSLayout>
  )
}
